import React from 'react';

import {Tabs} from '@mantine/core';
import {Text} from 'src/design/atoms/Text';
import type {PersonnelResponse} from 'src/api/ProactApiClient';

import {Subtitle} from 'src/design/atoms/Typography';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetPersonPermissionsQuery} from 'src/redux/queries/personnel';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAlert} from 'src/shared/hooks/useAlert';

import {EffectiveModulePermissions} from './components/EffectiveModulePermissions';
import {EffectiveObjectPermissions} from './components/EffectiveObjectPermissions';
import {ProjectAccessPermissions} from './components/ProjectAccessPermissions';
import {UserGroupMemberships} from './components/UserGroupMemberships';

export interface PersonnelPermissionsTabProps {
  item?: PersonnelResponse;
}
export const PersonnelPermissionsTab: React.FC<
  PersonnelPermissionsTabProps
> = ({item}) => {
  const {setAlert} = useAlert();
  const id = item?.id;

  const {data: permissions, isLoading, error} = useGetPersonPermissionsQuery(
    id!,
    {skip: id === undefined}
  );

  if (error) {
    setAlert(parseErrorMessage(error), 'error');
  }

  return (
    <>
      <Subtitle text="Permissions" />
      <Text c="dimmed" size="sm" mb="md">
        The permissions shown below are the highest effective permission this
        user has for each module based on their current user group memberships.
        To change their permissions, adjust the permissions of the user group or
        add/remove them from the user group.
      </Text>
      <Text c="dimmed" size="sm" mb="md">
        Permissions may be from multiple user groups. If the user is in multiple
        user groups, only one of those groups will be shown below.
      </Text>
      {isLoading ? (
        <TableBodyRender isLoading={isLoading} />
      ) : (
        <>
          <Tabs defaultValue="groups">
            <Tabs.List>
              <Tabs.Tab value="groups">User Group Memberships</Tabs.Tab>
              <Tabs.Tab value="module">Module Permissions</Tabs.Tab>
              <Tabs.Tab value="object">Object Permissions</Tabs.Tab>
              <Tabs.Tab value="project">Project Access</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="groups" pt="md">
              <UserGroupMemberships groups={permissions?.groups} />
            </Tabs.Panel>

            <Tabs.Panel value="module" pt="md">
              <EffectiveModulePermissions
                permissions={permissions?.modulePermissions}
              />
            </Tabs.Panel>

            <Tabs.Panel value="object" pt="md">
              <EffectiveObjectPermissions
                permissions={permissions?.objectPermissions}
              />
            </Tabs.Panel>

            <Tabs.Panel value="project" pt="md">
              <ProjectAccessPermissions
                permissions={permissions?.projectAccess}
              />
            </Tabs.Panel>
          </Tabs>
        </>
      )}
    </>
  );
};
